import { render, html, nothing } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import { repeat } from "lit-html/directives/repeat.js";
import connect from "@escapio/shared/data-store/connect";
import {
	ACTION_REMOVE_TOGGLE_FILTER,
	ACTION_SET_TOGGLE_FILTER,
	store,
} from "./DataStore";
import { FilterWithInitialLoadingStyle } from "../FilterWithInitialLoadingStyle";
import { getUsedFilters } from "@escapio/portal/state/UsedFilters";
import { translate } from "@escapio/shared/l10n/translate";
import { track, trackImpression } from "@escapio/portal/tracker/EventTracker";
import { getFilter } from "@escapio/portal/state/hotel-list/get-category-fitlers";
import { data } from "@escapio/shared/state/payload";

let visibilityTracked = false;

customElements.define(
	"esc-hotel-list-recently-used-filters",
	class extends connect(FilterWithInitialLoadingStyle, store) {
		constructor() {
			super();
			this.usedFilters = getUsedFilters();
		}

		update(state, { counters }) {
			if (!this.usableUsedFilters) {
				let possibleFilterCounters = this.getPossibleFilterCounters(
					state.allHotels,
				);

				this.usableUsedFilters = this.usedFilters.filter(
					filter =>
						possibleFilterCounters[filter.name][filter.value] > 0 &&
						filter.value != data.preselectedHotelType,
				);
			}

			const [initialFilters, additionalFilters] =
				this.usableUsedFilters.length > 8
					? [
							this.usableUsedFilters.slice(0, 6),
							this.usableUsedFilters.slice(6),
						]
					: [this.usableUsedFilters];

			if (this.usableUsedFilters.length > 0) {
				this.style.display = "";
				this.trackVisibility(this.usedFilters.length);
				this.removeInitialLoading();
			} else {
				this.style.display = "none";
			}

			const renderFilter = this.getFilterRenderer(state, counters);

			const template =
				this.usableUsedFilters.length < 1
					? nothing
					: html`
							<div
								class="filter-box__section-title expandable-filter__filter-name"
							>
								${translate("used_filters_label")}
							</div>

							<div class="expandable-filter">
								<div>
									<div class="expandable-filter__list">
										${repeat(
											initialFilters,
											({ value }) => value,
											renderFilter,
										)}
									</div>
									${
										additionalFilters
											? html` <div
													class="expandable-filter__list expandable-filter__additional-list"
												>
													${repeat(
														additionalFilters,
														({ name, value }) => [name, value],
														renderFilter,
													)}
												</div>`
											: nothing
									}
								</div>

								${
									additionalFilters
										? html`
												<esc-toggle
													class="expandable-filter__toggle-button"
													.toggableContainer=${this}
													toggable-selector=".expandable-filter__additional-list"
													@open=${() =>
														track(
															"Filter",
															"Meine Filter Nutzung",
															`aufklappen${this.trackLabelSuffix}`,
														)}
													@close=${() =>
														track(
															"Filter",
															"Meine Filter Nutzung",
															`zuklappen${this.trackLabelSuffix}`,
														)}
												>
													<button
														class="expandable-filter__show-more secondary-button"
													>
														<span
															class="expandable-filter__arrow fa fa-angle-down"
														></span>
														<span class="expandable-filter__show-more-text"
															>${translate("filter_show_more")}</span
														>
														<span class="expandable-filter__show-less-text"
															>${translate("filter_show_less")}</span
														>
													</button>
												</esc-toggle>
											`
										: nothing
								}
							</div>
						`;

			render(template, this, { host: this });
		}

		getFilterRenderer(state, counters) {
			return ({ name, value }, index) => {
				const { label } = getFilter(name, value);
				const hotelCount = counters[name][value];
				const disabled = hotelCount <= 0;
				const checked = state.filters[name].includes(value);

				return html`<label class="form-checkbox ${classMap({ disabled })}">
					<input
						class="form-checkbox__input"
						type="checkbox"
						@change=${this.onChange(name, label, value, index + 1)}
						?disabled=${disabled}
						.checked=${checked}
					/>
					<span class="form-checkbox__label">
						<span>
							${label}
							<span class="filter-hotel-count">${hotelCount}</span>
						</span>
					</span>
				</label>`;
			};
		}

		onChange(categoryName, name, value, position) {
			return event => {
				const checked = event.target.checked;
				this.dispatchAction(
					checked ? ACTION_SET_TOGGLE_FILTER : ACTION_REMOVE_TOGGLE_FILTER,
					{ name: categoryName, value },
				);
				track(
					"Filter",
					`Meine Filter${checked ? "" : " entfernen"}`,
					`${name} - ${position}${this.trackLabelSuffix}`,
				);
			};
		}

		getPossibleFilterCounters(hotels) {
			let counters = {};
			const fromEntries = entries =>
				entries.reduce(
					(acc, [key, value]) => Object.assign(acc, { [key]: value }),
					{},
				);

			let toggles = ["hotelTypes", "criteria"];
			toggles.forEach(name => {
				counters[name] = fromEntries(data[name].map(({ value }) => [value, 0]));
			});

			hotels.forEach(hotel => {
				toggles.forEach(name => {
					data[name].forEach(({ value }) => {
						if (hotel[name].includes(value)) {
							counters[name][value] = 1;
						}
					});
				});
			});

			return counters;
		}

		trackVisibility(filterLength) {
			if (visibilityTracked || this.offsetParent === null) {
				// Don't track if already done or element is not shown.
				return;
			}
			visibilityTracked = true;
			trackImpression(
				"Filter",
				"Meine Filter angezeigt",
				`${filterLength}${this.trackLabelSuffix}`,
			);
		}

		get trackLabelSuffix() {
			return this.getAttribute("track-label-suffix") || "";
		}
	},
);
